import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeContextValue {
  mode: ThemeMode;
  resolved: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
}

const STORAGE_KEY = 'cozzysim-theme';

const ThemeContext = createContext<ThemeContextValue>({
  mode: 'system',
  resolved: 'dark',
  setMode: () => {},
});

function getTelegramTheme(): ResolvedTheme | null {
  const scheme = window.Telegram?.WebApp?.colorScheme;
  if (scheme === 'light' || scheme === 'dark') return scheme;
  return null;
}

function getSystemTheme(): ResolvedTheme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveTheme(mode: ThemeMode): ResolvedTheme {
  if (mode === 'light' || mode === 'dark') return mode;
  return getTelegramTheme() ?? getSystemTheme();
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
    // Web defaults to dark (desktop mock); Telegram follows system/Telegram theme
    const inTelegram = Boolean(window.Telegram?.WebApp?.initData);
    return inTelegram ? 'system' : 'dark';
  });

  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(mode));

  const setMode = (next: ThemeMode) => {
    setModeState(next);
    localStorage.setItem(STORAGE_KEY, next);
  };

  useEffect(() => {
    const next = resolveTheme(mode);
    setResolved(next);
    document.documentElement.setAttribute('data-theme', next);
  }, [mode]);

  useEffect(() => {
    // Keep resolved in sync when system/Telegram theme changes under mode=system
    if (mode !== 'system') return;

    const apply = () => {
      const next = resolveTheme('system');
      setResolved(next);
      document.documentElement.setAttribute('data-theme', next);
    };

    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', apply);

    const tg = window.Telegram?.WebApp;
    tg?.onEvent?.('themeChanged', apply);

    return () => {
      mq.removeEventListener('change', apply);
      tg?.offEvent?.('themeChanged', apply);
    };
  }, [mode]);

  return (
    <ThemeContext.Provider value={{ mode, resolved, setMode }}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
